import { useState, useEffect } from 'react';
export class Form {
    constructor(data, rules) {
        this._validated = [];
        this._errors = new Map();
        this._setters = [];
        this.getRules = (k) => {
            const fieldRules = this._rules[k];
            if (!fieldRules)
                return undefined;
            return Array.isArray(fieldRules) ? fieldRules : [fieldRules];
        };
        this.clear = () => {
            this._validated = [];
            const allKeys = this._getAllKeys();
            for (const key of allKeys) {
                this._callSetter('validated', key, false);
                this.setError(key, undefined);
            }
        };
        this._getAllKeys = () => Object.keys(this._rules);
        this.validate = (k) => {
            const valid = this._validateField(k);
            this._validateFields(true, [k]);
            return valid;
        };
        this.validateAll = () => {
            return this._validateFields(false);
        };
        this._validateField = (k) => {
            if (!this.getValidated(k)) {
                this._validated.push(k);
                this._callSetter('validated', k, true);
            }
            const data = this._data;
            const rules = this.getRules(k);
            if (rules === undefined) {
                this.setError(k, undefined);
                return true;
            }
            let valid = true;
            for (const rule of rules) {
                const value = data[k];
                const ruleError = rule(value, this);
                if (ruleError !== undefined) {
                    valid = false;
                    this.setError(k, ruleError);
                    break;
                }
            }
            if (valid) {
                this.setError(k, undefined);
            }
            return valid;
        };
        this._validateFields = (onlyIfValidated, exceptKeys) => {
            const keys = this._getAllKeys();
            let valid = true;
            for (const key of keys) {
                if (exceptKeys && exceptKeys.indexOf(key) !== -1)
                    continue;
                if (onlyIfValidated && !this.getValidated(key)) {
                    valid = valid && false;
                }
                else {
                    const fieldValid = this._validateField(key);
                    valid = valid && fieldValid;
                }
            }
            return valid;
        };
        this.getValidated = (k) => this._validated.indexOf(k) !== -1;
        this.clearValidated = (k) => {
            const ind = this._validated.indexOf(k);
            if (ind !== -1) {
                this._validated.splice(ind, 1);
            }
        };
        /**
         * @deprecated This method will become deprecated in future versions. Please, use getValidated instead.
         */
        this.isValidated = (k) => this.getValidated(k);
        this.setError = (k, error) => {
            if (error === this.getError(k))
                return;
            this._errors.set(k, error);
            this._callSetter('error', k, error);
        };
        this.getError = (k) => this._errors.get(k);
        this.setValue = (key, value) => {
            if (value === this.getValue(key))
                return;
            this._data[key] = value;
            this._callSetter('value', key, value);
            this._validateFields(true);
        };
        this.getValue = (key) => this._data[key];
        this._callSetter = (event, key, value) => {
            return this._setters
                .filter(setter => setter.key === key && setter.type === event)
                .forEach(setter => setter.set(value));
        };
        this.addSetter = (setter) => {
            const ind = this._setters.indexOf(setter);
            if (ind === -1)
                this._setters.push(setter);
        };
        this.removeSetter = (setter) => {
            const ind = this._setters.indexOf(setter);
            if (ind !== -1)
                this._setters.splice(ind, 1);
        };
        this._rules = rules;
        this._data = data;
    }
    /**
     * @deprecated This method will become deprecated in future versions. Please, use getRules instead.
     */
    get rules() { return this._rules; }
}
export const useForm = (data, rules) => {
    return useState(() => new Form(data, rules))[0];
};
const createHook = (form, key, type, initialState) => {
    const [state, setState] = useState(initialState());
    useEffect(() => {
        const keySetter = { key, type: type, set: setState };
        form.addSetter(keySetter);
        return () => {
            form.removeSetter(keySetter);
        };
    }, [key, setState, form]);
    return state;
};
export const useError = (form, key) => {
    return createHook(form, key, 'error', () => form.getError(key));
};
export const useValue = (form, key) => {
    return createHook(form, key, 'value', () => form.getValue(key));
};
export const useValidated = (form, key) => {
    return createHook(form, key, 'validated', () => form.getValidated(key));
};
export const useField = (form, key) => {
    return [useValue(form, key), useError(form, key), useValidated(form, key)];
};
